import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#854DFF] via-[#7A42E6] to-[#6F37CC] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-white hover:text-[#B19CFF] transition-colors duration-200 mb-6"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-[#B19CFF] max-w-3xl mx-auto">
              Last Updated: January 23, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">

            <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-6">
              These Terms of Service ("Terms") govern your use of the CredFlexi agent-driven commerce platform and related services (collectively, the "Services") provided by CredFlexi, a DBA (doing business as) of Essonsories Inc., a Delaware corporation ("CredFlexi," "we," "us," or "our").
            </p>
            <p className="text-gray-700 mb-8">
              By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use our Services.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Description of Services</h2>
            <p className="text-gray-700 mb-4">
              CredFlexi provides an enterprise-grade agent-driven commerce platform that includes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li><strong>Agent-Driven Platform:</strong> AI-powered autonomous agents for complex transaction management</li>
              <li><strong>Marketplace Creation:</strong> Industry-specific digital marketplace development</li>
              <li><strong>Financial Orchestration:</strong> Embedded banking and finance automation services</li>
              <li><strong>Analytics & Intelligence:</strong> Predictive analytics and business intelligence tools</li>
              <li><strong>Professional Services:</strong> Implementation, consulting, and support services</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Eligibility and Account Registration</h2>
            <p className="text-gray-700 mb-4">
              Our Services are intended for business use only. To use our Services, you must:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Be at least 18 years old and have the legal capacity to enter into agreements</li>
              <li>Represent a legitimate business entity</li>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your account credentials</li>
            </ul>
            <p className="text-gray-700 mb-8">
              You are responsible for all activities that occur under your account and must notify us immediately of any unauthorized use.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Acceptable Use Policy</h2>
            <p className="text-gray-700 mb-4">
              You agree to use our Services only for lawful purposes and in accordance with these Terms. You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li>Use the Services for any illegal, fraudulent, or unauthorized purpose</li>
              <li>Interfere with or disrupt the Services or servers connected to the Services</li>
              <li>Attempt to gain unauthorized access to any portion of the Services</li>
              <li>Upload, transmit, or distribute malicious code or content</li>
              <li>Violate any applicable local, state, national, or international law</li>
              <li>Infringe upon the intellectual property rights of others</li>
              <li>Engage in any activity that could harm CredFlexi's reputation</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Intellectual Property Rights</h2>
            <p className="text-gray-700 mb-6">
              The Services and all content, features, and functionality are owned by CredFlexi and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-gray-700 mb-6">
              We grant you a limited, non-exclusive, non-transferable license to access and use the Services for your internal business purposes, subject to these Terms.
            </p>
            <p className="text-gray-700 mb-8">
              You retain ownership of any content, data, or information you submit through the Services ("Customer Data"), while granting us the necessary rights to provide the Services.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Privacy and Data Protection</h2>
            <p className="text-gray-700 mb-6">
              Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.
            </p>
            <p className="text-gray-700 mb-8">
              By using our Services, you acknowledge that you have read and understood our Privacy Policy and consent to the collection, use, and disclosure of your information as described therein.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Payment Terms</h2>
            <p className="text-gray-700 mb-4">
              For paid Services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Fees are based on the service tier and usage as specified in your service agreement</li>
              <li>Payment is due according to the billing terms in your service agreement</li>
              <li>All fees are non-refundable unless otherwise specified in writing</li>
              <li>We may suspend or terminate Services for non-payment</li>
              <li>Price changes will be communicated with at least 30 days' notice</li>
            </ul>
            <p className="text-gray-700 mb-8">
              Enterprise clients will receive detailed service agreements outlining specific pricing, terms, and service level agreements.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Service Level and Support</h2>
            <p className="text-gray-700 mb-4">
              We strive to provide reliable Services with the following commitments:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li><strong>Uptime:</strong> We target 99.99% uptime for our enterprise services</li>
              <li><strong>Support:</strong> Business hours support via email and dedicated enterprise phone support</li>
              <li><strong>Security:</strong> Industry-standard security measures and compliance certifications</li>
              <li><strong>Performance:</strong> API response times typically under 50ms</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Limitation of Liability</h2>
            <p className="text-gray-700 mb-6">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL CREDFLEXI OR ESSONSORIES INC. BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICES.
            </p>
            <p className="text-gray-700 mb-8">
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM OR RELATING TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID US FOR THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Indemnification</h2>
            <p className="text-gray-700 mb-8">
              You agree to indemnify, defend, and hold harmless CredFlexi, Essonsories Inc., and their officers, directors, employees, and agents from and against any claims, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising from or relating to your use of the Services, violation of these Terms, or infringement of any third-party rights.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Termination</h2>
            <p className="text-gray-700 mb-4">
              These Terms remain in effect until terminated by either party. We may terminate or suspend your access to the Services at any time, with or without cause, with or without notice, for any reason including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Violation of these Terms</li>
              <li>Non-payment of fees</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Long periods of inactivity</li>
            </ul>
            <p className="text-gray-700 mb-8">
              Upon termination, your right to use the Services will cease immediately, and we may delete your account and Customer Data.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Modifications to Terms</h2>
            <p className="text-gray-700 mb-8">
              We reserve the right to modify these Terms at any time. We will provide notice of material changes through our website, email, or other reasonable means. Your continued use of the Services after the effective date of any changes constitutes acceptance of the revised Terms.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Governing Law and Dispute Resolution</h2>
            <p className="text-gray-700 mb-6">
              These Terms are governed by and construed in accordance with the laws of the State of Delaware, without regard to conflict of law principles.
            </p>
            <p className="text-gray-700 mb-6">
              Any disputes arising under these Terms shall be resolved through binding arbitration in Delaware, except that either party may seek injunctive relief in any court of competent jurisdiction.
            </p>
            <p className="text-gray-700 mb-8">
              For enterprise clients, specific dispute resolution procedures may be outlined in separate service agreements.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Compliance and Regulatory</h2>
            <p className="text-gray-700 mb-4">
              Our Services are designed to comply with applicable regulations including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-8 space-y-2">
              <li>SOC 2 Type II compliance</li>
              <li>GDPR for European data subjects</li>
              <li>CCPA for California residents</li>
              <li>PCI DSS for payment processing</li>
              <li>Industry-specific regulations as applicable</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">15. Miscellaneous</h2>
            <p className="text-gray-700 mb-4">
              <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy and any applicable service agreements, constitute the entire agreement between you and CredFlexi.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Assignment:</strong> You may not assign these Terms without our written consent. We may assign these Terms without restriction.
            </p>
            <p className="text-gray-700 mb-8">
              <strong>Force Majeure:</strong> We shall not be liable for any failure to perform due to circumstances beyond our reasonable control.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">16. Contact Information</h2>
            <div className="bg-[#854DFF]/5 p-6 rounded-lg mb-8">
              <p className="text-gray-700 mb-4">
                <strong>CredFlexi (DBA of Essonsories Inc.)</strong>
              </p>
              <p className="text-gray-700 mb-2">
                16192 Coastal Highway<br />
                Lewes, Delaware 19958<br />
                United States
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-[#854DFF]" />
                  <a href="mailto:legal@credflexi.com" className="text-[#854DFF] hover:text-[#7A42E6]">
                    legal@credflexi.com
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-[#854DFF]" />
                  <a href="mailto:info@credflexi.com" className="text-[#854DFF] hover:text-[#7A42E6]">
                    info@credflexi.com
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-[#854DFF]" />
                  <span className="text-gray-700">Enterprise Support Available 24/7</span>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <p className="text-sm text-gray-500 text-center">
                These Terms of Service are effective as of January 23, 2025, and apply to all users of CredFlexi services provided by Essonsories Inc.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;